import productTypes from 'constants/productTypes';
import { add } from 'ramda';
import { DB_FEE_CODE, marginTariff } from 'constants/tariffCodes';

const isDbFee = (fee) => fee.code === DB_FEE_CODE && fee.provisional === true && !fee.cancelled;

const calculateBookingItemTotal = (bookingItem) => {
  let price = 0;

  bookingItem.forEach((item) => {
    if (item.cancelled && item.provisional) {
      price = add(price, item.price);
    }
  });

  return price;
};

const calculateBookingItemLoyaltyTotal = (bookingItem) => {
  let loyaltyPoints = 0;

  bookingItem.forEach((item) => {
    if (item.cancelled && item.provisional) {
      loyaltyPoints = add(loyaltyPoints, item.loyaltyPoints);
    }
  });

  return loyaltyPoints;
};

const marginCodes = new Set(Object.values(marginTariff));

const isRefundableExtra = (item) =>
  item?.isPendingCancellation === true && item?.type !== productTypes.EXTERNAL_TICKET && !marginCodes.has(item?.code);

const calculateExtraProductsTotal = (products) => {
  let price = 0;

  products.forEach((item) => {
    if (isRefundableExtra(item)) {
      price = add(price, item.price || 0);
    }
  });

  return price;
};

const getTicketTotal = (segments) => {
  let totalTicketsPrice = 0;

  segments.forEach((segment) => {
    const addOnLegTotal =
      (segment.addOnLeg && segment.addOnLeg.bookingItems && calculateBookingItemTotal(segment.addOnLeg.bookingItems)) || 0;
    const legTotal = calculateBookingItemTotal(segment.bookingItems);
    totalTicketsPrice = add(totalTicketsPrice, add(addOnLegTotal, legTotal));
  });

  return totalTicketsPrice;
};

const getExtrasTotal = (segments) => {
  let totalExtrasPrice = 0;

  segments.forEach((segment) => {
    const legTotal = calculateExtraProductsTotal(segment.extraProducts);
    totalExtrasPrice = add(totalExtrasPrice, legTotal);
  });

  return totalExtrasPrice;
};

const getExtrasFees = (segments) => {
  let totalExtrasFees = 0;
  const extrasItemRefs = segments
    .map((leg) =>
      leg?.extraProducts?.filter((e) => e?.type !== productTypes.EXTERNAL_TICKET && !marginCodes.has(e?.code)).map((e) => e.itemRef)
    )
    .flat();

  const feesItemRefs = segments
    .map((leg) => leg?.fees?.filter((fee) => !isDbFee(fee)).map((fee) => ({ price: fee.price, itemRef: fee.itemRef })))
    .flat();

  const extrasFees = feesItemRefs.filter((fee) => extrasItemRefs.includes(fee?.itemRef)).map((fee) => fee?.price);

  if (extrasFees.length) {
    totalExtrasFees = extrasFees.reduce((acc, price) => acc + price, totalExtrasFees);
  }

  return totalExtrasFees;
};

const getProvisionalTicketTotal = (segments) => {
  const totalTicketsPrice = segments.reduce((total, segment) => {
    const items = [...segment.bookingItems];
    if (segment.addOnLeg && segment.addOnLeg.bookingItems) {
      items.push(...segment.addOnLeg.bookingItems);
    }

    const segmentTotal = items.reduce((price, item) => (!item.cancelled && item.provisional ? add(price, item.price) : price), 0);

    return add(total, segmentTotal);
  }, 0);

  return totalTicketsPrice;
};

const getExtrasWithCode = (segments, marginCode) =>
  segments.reduce((tot, { extraProducts = [] }) => {
    const margin = extraProducts.find(({ cancelled, provisional, code }) => (!cancelled || provisional) && code === marginCode) || {
      priceWithCurrency: 0
    };

    return add(tot, margin.priceWithCurrency);
  }, 0);

const calculateBookingItemFee = (bookingItem) => {
  let price = 0;

  bookingItem.forEach((item) => {
    if (item.provisional) {
      price = add(price, item.price);
    }
  });

  return price;
};

const getTrainLoyaltyPoints = (segments) => {
  let totalLoyaltyPoints = 0;

  segments.forEach((segment) => {
    const addOnLegTotal =
      (segment.addOnLeg && segment.addOnLeg.bookingItems && calculateBookingItemLoyaltyTotal(segment.addOnLeg.bookingItems)) || 0;
    const legTotal = calculateBookingItemLoyaltyTotal(segment.bookingItems);
    totalLoyaltyPoints = add(totalLoyaltyPoints, add(addOnLegTotal, legTotal));
  });

  return totalLoyaltyPoints;
};

const toNegativeValue = (amount) => {
  if (amount > 0) {
    return amount * -1;
  }

  return amount;
};

const getTrainFees = (segments) => {
  let totalFeePrice = 0;

  segments.forEach((segment) => {
    const segmentFee = calculateBookingItemFee(segment.fees);
    totalFeePrice = add(totalFeePrice, segmentFee);
  });

  return toNegativeValue(totalFeePrice);
};

const getHotelsPrice = (hotels = []) =>
  hotels && hotels.reduce((total, { cancelled, provisional, price }) => (!cancelled || provisional ? add(total, price) : total), 0);

const getTrainDeprecatedMargin = (hotels = []) =>
  hotels.reduce((total, { deprecatedTrainMargin, cancelled }) => (cancelled ? total : add(total, deprecatedTrainMargin)), 0);

const getHotelsMargin = (hotels = []) =>
  hotels && hotels.reduce((total, { cancelled, provisional, margin }) => (!cancelled || provisional ? add(total, margin) : total), 0);

const getFeeAmount = (fees = []) =>
  fees && fees.reduce((total, { cancelled, provisional, price }) => (!cancelled || provisional ? add(total, price) : total), 0);

const getDbFeeAmount = (segments) => {
  let total = 0;
  segments.forEach((segment) => {
    (segment.fees || []).forEach((fee) => {
      if (isDbFee(fee)) {
        total = add(total, fee.price);
      }
    });
  });
  return total;
};

export {
  getExtrasTotal,
  getExtrasFees,
  getTicketTotal,
  getProvisionalTicketTotal,
  getTrainFees,
  getHotelsPrice,
  getFeeAmount,
  getExtrasWithCode,
  getHotelsMargin,
  getTrainDeprecatedMargin,
  getDbFeeAmount,
  isDbFee,
  getTrainLoyaltyPoints
};
